import React from "react";
import ServiceFactory from "../../service";

/*
 * Interest sparkline — distinct viewers/day for one product from
 * GET /v1/product/{productId}/interest-trend/{range} (PP refresh item 5).
 *
 * The endpoint returns only days WITH viewers, so the series is re-densified
 * over the observed date span before rendering. Fetches are cached per
 * (productId, range) module-wide and run through a small concurrency gate so
 * a 50-row page doesn't fire 50 simultaneous calls.
 */

const cache = new Map();
const queue = [];
let active = 0;
const MAX_ACTIVE = 5;
function pump() {
  while (active < MAX_ACTIVE && queue.length) {
    const job = queue.shift();
    active += 1;
    job().finally(() => {
      active -= 1;
      pump();
    });
  }
}
function fetchTrend(productId, range) {
  const key = `${productId}|${range}`;
  if (cache.has(key)) return cache.get(key);
  const p = new Promise(resolve => {
    queue.push(async () => {
      try {
        const api = `/product/${encodeURIComponent(productId)}/interest-trend/${range}`;
        const Service = ServiceFactory.getType(api, "product");
        const rows = await new Service().getAsync();
        resolve(Array.isArray(rows) ? rows : []);
      } catch (e) {
        resolve(null);
      }
    });
    pump();
  });
  cache.set(key, p);
  return p;
}
export default class InterestSpark extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      points: undefined
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (prev.productId !== this.props.productId || prev.range !== this.props.range) {
      this.setState({
        points: undefined
      });
      this.load();
    }
  }
  async load() {
    const {
      productId,
      range
    } = this.props;
    // all_time can span years of day-grain rows — not a sparkline shape.
    if (!productId || range === "all_time") {
      this.setState({
        points: null
      });
      return;
    }
    const rows = await fetchTrend(productId, range);
    if (!this._alive) return;
    if (!rows || rows.length === 0) {
      this.setState({
        points: null
      });
      return;
    }
    // densify over the observed span (missing days = 0 viewers)
    const byDay = {};
    rows.forEach(r => {
      byDay[String(r.date).slice(0, 10)] = Number(r.viewers) || 0;
    });
    const days = Object.keys(byDay).sort();
    const start = new Date(days[0] + "T00:00:00");
    const end = new Date(days[days.length - 1] + "T00:00:00");
    const series = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10);
      series.push(byDay[key] || 0);
    }
    this.setState({
      points: series
    });
  }
  render() {
    const {
      points
    } = this.state;
    if (points === undefined) return /*#__PURE__*/React.createElement("span", {
      className: "interest-spark pending"
    });
    if (!points || points.length < 2) return /*#__PURE__*/React.createElement("span", {
      className: "territory-hint"
    }, "\u2014");
    const W = 64,
      H = 16,
      PAD = 1;
    const max = Math.max(...points, 1);
    const step = (W - PAD * 2) / (points.length - 1);
    const pts = points.map((v, i) => `${(PAD + i * step).toFixed(1)},${(H - PAD - v / max * (H - PAD * 2)).toFixed(1)}`).join(" ");
    const total = points.reduce((a, b) => a + b, 0);
    return /*#__PURE__*/React.createElement("svg", {
      className: "interest-spark",
      width: W,
      height: H,
      viewBox: `0 0 ${W} ${H}`
    }, /*#__PURE__*/React.createElement("title", null, `${total} viewer-days across ${points.length} days (peak ${max}/day)`), /*#__PURE__*/React.createElement("polyline", {
      points: pts,
      fill: "none",
      stroke: "#2d73f5",
      strokeWidth: "1.5",
      strokeLinejoin: "round",
      strokeLinecap: "round"
    }));
  }
}